import React from 'react';
import { FiX, FiRotateCcw } from 'react-icons/fi';
import styles from './Emitir.module.css';
import useEsc from '../../../hooks/useEsc';
import CampoMoeda from '../../../componentes/CampoMoeda';

/* Modais dos campos que saíram da tela principal. Um componente só, com o
   conteúdo trocando por `tipo`, pra não repetir a moldura do modal quatro
   vezes.

   Cada bloco informa quando está fora do padrão (`resumo`), e o cabeçalho
   usa isso pra marcar o ícone -- campo escondido sem aviso é como se emite
   nota errada sem perceber. */

const SITUACAO_PIS_COFINS = [
  { v: '', l: 'Não informar' },
  { v: '1', l: '1 - Operação tributável' },
  { v: '2', l: '2 - Operação não tributável' },
  { v: '3', l: '3 - Operação isenta' },
  { v: '4', l: '4 - Operação imune' },
  { v: '5', l: '5 - Operação com suspensão' },
];

export const EXTRAS = {
  descontos: {
    titulo: 'Descontos',
    sub: 'Raramente usados. Em branco, a nota sai sem desconto.',
    campos: ['descontoIncondicionado', 'descontoCondicionado'],
    resumo: (d) => {
      const i = Number(d.descontoIncondicionado || 0);
      const c = Number(d.descontoCondicionado || 0);
      if (!i && !c) return null;
      return `desconto ${[i && `incond. ${i}`, c && `cond. ${c}`].filter(Boolean).join(' / ')}`;
    },
  },
  complementares: {
    titulo: 'Informações complementares',
    sub: 'Texto livre e documentos de referência que vão impressos na nota.',
    campos: ['docResponsabilidadeTecnica', 'numeroPedido', 'documentoReferencia', 'informacoesComplementares'],
    resumo: (d) => (d.docResponsabilidadeTecnica || d.numeroPedido || d.documentoReferencia
      || d.informacoesComplementares) ? 'informações complementares preenchidas' : null,
  },
  federal: {
    titulo: 'Tributação federal',
    sub: 'Normalmente vem dos padrões do cliente. Preencha aqui só pra sobrescrever nesta nota.',
    campos: ['situacaoPisCofins', 'irrf', 'contribuicoesRetidas', 'contribuicaoPrevidenciaria'],
    resumo: (d) => (d.situacaoPisCofins || Number(d.irrf || 0) || Number(d.contribuicoesRetidas || 0)
      || Number(d.contribuicaoPrevidenciaria || 0)) ? 'tributação federal informada à mão' : null,
  },
  tributos: {
    titulo: 'Valor aproximado dos tributos',
    sub: 'Lei da transparência. O padrão vem do percentual configurado no cliente.',
    campos: ['valorAproximadoTributos'],
    resumo: (d) => Number(d.valorAproximadoTributos || 0)
      ? `tributos aprox. ${d.valorAproximadoTributos}` : null,
  },
};

/* o que está fora do padrão, somando todos os extras -- usado no ícone e na
   tela de revisão */
export function extrasAlterados(dados) {
  return Object.values(EXTRAS).map(e => e.resumo(dados)).filter(Boolean);
}

function Campo({ nome, dados, set }) {
  const comuns = { className: styles.entrada, value: dados[nome] ?? '' };
  switch (nome) {
    case 'descontoIncondicionado':
      return <><span className={styles.rotulo}>Desconto incondicionado (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} />
        <span className={styles.dica}>Abate da base de cálculo do ISSQN.</span></>;
    case 'descontoCondicionado':
      return <><span className={styles.rotulo}>Desconto condicionado (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} />
        <span className={styles.dica}>Não abate a base de cálculo.</span></>;
    case 'docResponsabilidadeTecnica':
      return <><span className={styles.rotulo}>Nº do documento de responsabilidade técnica</span>
        <input {...comuns} onChange={e => set(nome, e.target.value)} /></>;
    case 'numeroPedido':
      return <><span className={styles.rotulo}>Nº do pedido / ordem de compra (B2B)</span>
        <input {...comuns} onChange={e => set(nome, e.target.value)} /></>;
    case 'documentoReferencia':
      return <><span className={styles.rotulo}>Documento de referência</span>
        <textarea {...comuns} rows={2} onChange={e => set(nome, e.target.value)} /></>;
    case 'informacoesComplementares':
      return <><span className={styles.rotulo}>Informações complementares</span>
        <textarea {...comuns} rows={3} maxLength={2000} onChange={e => set(nome, e.target.value)} /></>;
    case 'situacaoPisCofins':
      return <><span className={styles.rotulo}>Situação tributária do PIS/COFINS</span>
        <select {...comuns} onChange={e => set(nome, e.target.value)}>
          {SITUACAO_PIS_COFINS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select></>;
    case 'irrf':
      return <><span className={styles.rotulo}>IRRF retido (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} /></>;
    case 'contribuicoesRetidas':
      return <><span className={styles.rotulo}>Contribuições sociais retidas (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} /></>;
    case 'contribuicaoPrevidenciaria':
      return <><span className={styles.rotulo}>INSS retido (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} />
        <span className={styles.dica}>Em branco usa o padrão do cliente.</span></>;
    case 'valorAproximadoTributos':
      return <><span className={styles.rotulo}>Valor aproximado dos tributos (R$)</span>
        <CampoMoeda {...comuns} onChange={v => set(nome, v)} />
        <span className={styles.dica}>Em branco usa o percentual configurado no cliente.</span></>;
    default:
      return null;
  }
}

function ModalExtras({ tipo, dados, set, onFechar }) {
  useEsc(onFechar);
  const cfg = EXTRAS[tipo];
  if (!cfg) return null;
  const alterado = cfg.resumo(dados);

  function limpar() { cfg.campos.forEach(c => set(c, '')); }

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa} style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTopo}>
          <div>
            <h3 className={styles.modalTitulo}>{cfg.titulo}</h3>
            <p className={styles.modalSub}>{cfg.sub}</p>
          </div>
          <button type="button" className={styles.btnIcone} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo}>
          <div className={styles.grade}>
            {cfg.campos.map(nome => (
              <div key={nome} className={styles.campo}>
                <Campo nome={nome} dados={dados} set={set} />
              </div>
            ))}
          </div>
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={limpar} disabled={!alterado}>
            <FiRotateCcw size={13} /> Limpar
          </button>
          <button type="button" className={styles.btnAvancar} onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

export default ModalExtras;
